import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { GraduationCap, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { apiError } from "@/lib/api";
import { authService } from "@/services/authService";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { EarlyAccessModal } from "@/components/EarlyAccessModal";

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [searchParams] = useSearchParams();
  const codeParam = (searchParams.get("code") || "").toUpperCase();
  const [promoCode, setPromoCode] = useState(codeParam);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Airtight private-beta gate: the registration form is only revealed once a
  // valid invite code is present in the URL. Any other path lands on the gate,
  // which presents the Invite Code Modal (with the waitlist fallback).
  const [gate, setGate] = useState({ checking: true, valid: false });
  const [gateOpen, setGateOpen] = useState(true);
  useEffect(() => {
    let alive = true;
    if (!codeParam) { setGate({ checking: false, valid: false }); return; }
    authService.validateCode(codeParam)
      .then((res) => { if (alive) setGate({ checking: false, valid: !!res.valid }); })
      .catch(() => { if (alive) setGate({ checking: false, valid: false }); });
    return () => { alive = false; };
  }, [codeParam]);

  // Keep the promo field in sync with the URL code even when the component is
  // already mounted (in-SPA gate → /register?code=... transition). Without this
  // the read-only field stays blank and the code is never sent to /register.
  useEffect(() => { setPromoCode(codeParam); }, [codeParam]);

  const submit = async (e) => {
    e.preventDefault();
    if (!agreed) {
      toast.error("Please agree to the Terms of Service, Privacy Policy, and Acceptable Use Policy to continue.");
      return;
    }
    setLoading(true);
    try {
      const data = await authService.register({ name, email, password, promo_code: promoCode || undefined });
      setSession(data.token, data.user);
      toast.success(data.user.plan_type === "scholar" ? "Beta code applied — welcome, Scholar! 🎓" : "Account created. Let's learn!");
      const params = new URLSearchParams(location.search);
      params.delete("code");
      const qs = params.toString();
      navigate(qs ? `/learn?${qs}` : "/dashboard?welcome=1");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (gate.checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="register-gate-loading">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  if (!gate.valid) {
    return (
      <div className="min-h-screen grid md:grid-cols-2" data-testid="register-gate">
        <div className="hidden md:flex flex-col justify-between bg-foreground text-background p-12 text-grain">
          <Link to="/" className="flex items-center gap-2 font-display font-extrabold text-lg">
            <GraduationCap className="h-6 w-6" /> Learn Me
          </Link>
          <div>
            <h1 className="font-display text-5xl font-extrabold tracking-tighter leading-none">Private beta.<br />Invite only.</h1>
            <p className="mt-4 text-background/70 max-w-sm">Enter your invite code to create your account.</p>
          </div>
          <span className="text-background/50 text-sm">© 2026 Learn Me</span>
        </div>

        <div className="flex items-center justify-center p-6 md:p-12">
          <div className="w-full max-w-sm text-center">
            <div className="h-12 w-12 bg-primary/10 flex items-center justify-center mx-auto mb-5">
              <Lock className="h-6 w-6 text-primary" />
            </div>
            <h2 className="font-display text-3xl font-bold tracking-tight mb-2">You need an invite</h2>
            <p className="text-muted-foreground text-sm mb-6">Learn Me is currently in private beta. Enter your invite code to get started.</p>
            <Button onClick={() => setGateOpen(true)} className="rounded-none h-11 px-6 bg-primary hover:opacity-90" data-testid="register-gate-open">
              Enter invite code
            </Button>
            <p className="mt-6 text-sm text-muted-foreground">
              Already have an account? <Link to="/login" className="text-primary font-semibold" data-testid="gate-goto-login">Log in</Link>
            </p>
          </div>
        </div>

        <EarlyAccessModal open={gateOpen} onOpenChange={setGateOpen} />
      </div>
    );
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between bg-foreground text-background p-12 text-grain">
        <Link to="/" className="flex items-center gap-2 font-display font-extrabold text-lg">
          <GraduationCap className="h-6 w-6" /> Learn Me
        </Link>
        <div>
          <h1 className="font-display text-5xl font-extrabold tracking-tighter leading-none">Start learning<br />in seconds.</h1>
          <p className="mt-4 text-background/70 max-w-sm">Create a free account and generate your first course right away.</p>
        </div>
        <span className="text-background/50 text-sm">© 2026 Learn Me</span>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="register-form">
          <h2 className="font-display text-3xl font-bold tracking-tight mb-1">Create account</h2>
          <p className="text-muted-foreground text-sm mb-8">Free to start. No card required.</p>

          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider mb-2 block">Name</Label>
              <Input required value={name} onChange={(e) => setName(e.target.value)}
                className="rounded-none h-11" placeholder="Don" data-testid="register-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider mb-2 block">Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="rounded-none h-11" placeholder="you@example.com" data-testid="register-email-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider mb-2 block">Password</Label>
              <PasswordInput required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
                className="rounded-none h-11" placeholder="At least 8 characters" data-testid="register-password-input"
                toggleTestId="register-password-toggle" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider mb-2 block">Beta invite code</Label>
              <Input value={promoCode} readOnly
                className="rounded-none h-11 uppercase bg-secondary/50 cursor-not-allowed" data-testid="register-promo-input" />
            </div>
          </div>

          <label className="flex items-start gap-3 mt-6 cursor-pointer" data-testid="terms-agree-label">
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-primary shrink-0" data-testid="register-terms-checkbox" />
            <span className="text-sm text-muted-foreground leading-snug">
              I agree to the{" "}
              <Link to="/terms" target="_blank" className="text-primary font-semibold" data-testid="register-terms-link">Terms of Service</Link>
              {", "}
              <Link to="/privacy" target="_blank" className="text-primary font-semibold" data-testid="register-privacy-link">Privacy Policy</Link>
              {", and "}
              <Link to="/acceptable-use" target="_blank" className="text-primary font-semibold" data-testid="register-acceptable-use-link">Acceptable Use Policy</Link>.
            </span>
          </label>

          <Button type="submit" disabled={loading || !agreed} className="w-full mt-6 rounded-none h-11 bg-primary hover:opacity-90 disabled:opacity-50" data-testid="register-submit-button">
            {loading ? "Creating..." : "Create account"}
          </Button>

          <p className="mt-6 text-sm text-muted-foreground text-center">
            Already have an account? <Link to="/login" className="text-primary font-semibold" data-testid="goto-login">Log in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
